/**
 * CalibrationScreen — 9-point calibration for webcam eye tracking.
 *
 * User clicks each target dot. WebGazer uses these clicks to build
 * a mapping between eye features and screen positions.
 */

import { useState, useCallback } from 'react'
import { gazeTracker } from '@/lib/gaze-tracker'

interface CalibrationScreenProps {
  onComplete: () => void
  onCancel: () => void
}

// 13-point calibration: 9-point grid + 4 center-area points for reading zone accuracy
const CALIBRATION_POINTS = [
  // Corners and edges (coarse calibration)
  { x: 10, y: 8 },  { x: 50, y: 8 },  { x: 90, y: 8 },
  { x: 10, y: 50 }, { x: 50, y: 50 }, { x: 90, y: 50 },
  { x: 10, y: 92 }, { x: 50, y: 92 }, { x: 90, y: 92 },
  // Extra points in reading zone (center area where text lives)
  { x: 30, y: 30 }, { x: 70, y: 30 },
  { x: 30, y: 70 }, { x: 70, y: 70 },
]

const CLICKS_PER_POINT = 3 // 3 clicks × 13 points = 39 samples (good balance)

export function CalibrationScreen({ onComplete, onCancel }: CalibrationScreenProps) {
  const [currentPoint, setCurrentPoint] = useState(0)
  const [clicksOnCurrent, setClicksOnCurrent] = useState(0)
  const [phase, setPhase] = useState<'intro' | 'calibrating' | 'done'>('intro')

  const totalPoints = CALIBRATION_POINTS.length
  const progress = Math.round(((currentPoint * CLICKS_PER_POINT + clicksOnCurrent) / (totalPoints * CLICKS_PER_POINT)) * 100)

  const handlePointClick = useCallback((screenX: number, screenY: number) => {
    // Record this click position for WebGazer calibration
    gazeTracker.recordCalibrationClick(screenX, screenY)

    const newClicks = clicksOnCurrent + 1
    if (newClicks >= CLICKS_PER_POINT) {
      // Move to next point
      const nextPoint = currentPoint + 1
      if (nextPoint >= totalPoints) {
        setPhase('done')
        setTimeout(onComplete, 800)
      } else {
        setCurrentPoint(nextPoint)
        setClicksOnCurrent(0)
      }
    } else {
      setClicksOnCurrent(newClicks)
    }
  }, [currentPoint, clicksOnCurrent, totalPoints, onComplete])

  if (phase === 'intro') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', background: '#0f172a',
        color: '#e2e8f0', fontFamily: 'Inter, system-ui, sans-serif', padding: '40px',
      }}>
        <h2 style={{ fontSize: '28px', fontWeight: 600, marginBottom: '16px' }}>
          👁️ Calibrate Eye Tracking
        </h2>
        <p style={{ fontSize: '16px', color: '#94a3b8', maxWidth: '500px', textAlign: 'center', marginBottom: '8px' }}>
          Look at each dot and click it {CLICKS_PER_POINT} times. This teaches the
          system where you're looking on screen.
        </p>
        <p style={{ fontSize: '14px', color: '#64748b', maxWidth: '500px', textAlign: 'center', marginBottom: '32px' }}>
          Keep your head relatively still and sit at a comfortable distance from your screen.
          The calibration takes about 30 seconds.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setPhase('calibrating')} style={{
            padding: '12px 32px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #38bdf8, #818cf8)',
            color: '#fff', fontSize: '16px', fontWeight: 600, cursor: 'pointer',
          }}>
            Start Calibration
          </button>
          <button onClick={onCancel} style={{
            padding: '12px 24px', borderRadius: '10px',
            border: '1px solid #334155', background: 'transparent',
            color: '#94a3b8', fontSize: '16px', cursor: 'pointer',
          }}>
            Skip (use mouse)
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'done') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', background: '#0f172a',
        color: '#e2e8f0', fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <h2 style={{ fontSize: '24px', fontWeight: 600 }}>Calibration Complete!</h2>
        <p style={{ fontSize: '16px', color: '#94a3b8', marginTop: '8px' }}>
          Starting reader...
        </p>
      </div>
    )
  }

  // Calibrating phase
  const point = CALIBRATION_POINTS[currentPoint]!

  return (
    <div style={{
      position: 'fixed', inset: 0, width: '100vw', height: '100vh',
      background: '#0f172a', cursor: 'crosshair', zIndex: 100000,
    }}>
      {/* Progress bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
        background: '#1e293b',
      }}>
        <div style={{
          height: '100%', width: `${progress}%`,
          background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
          transition: 'width 0.3s ease-out',
        }} />
      </div>

      {/* Instructions */}
      <div style={{
        position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
        fontSize: '14px', color: '#64748b', textAlign: 'center',
        fontFamily: 'Inter, system-ui, sans-serif', zIndex: 100001,
      }}>
        Point {currentPoint + 1} of {totalPoints} — Click {CLICKS_PER_POINT - clicksOnCurrent} more time{CLICKS_PER_POINT - clicksOnCurrent !== 1 ? 's' : ''}
      </div>

      {/* Calibration target */}
      <div
        onClick={(e) => handlePointClick(e.clientX, e.clientY)}
        style={{
          position: 'absolute',
          left: `${point.x}%`,
          top: `${point.y}%`,
          transform: 'translate(-50%, -50%)',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #38bdf8 30%, rgba(56,189,248,0.3) 70%)',
          border: '2px solid #38bdf8',
          cursor: 'pointer',
          animation: 'pulse 1.5s ease-in-out infinite',
          boxShadow: '0 0 20px rgba(56,189,248,0.4)',
          zIndex: 100001,
        }}
      >
        {/* Inner dot */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '8px', height: '8px', borderRadius: '50%',
          background: '#fff',
        }} />
      </div>

      {/* Skip button */}
      <button onClick={onCancel} style={{
        position: 'absolute', bottom: '20px', right: '20px',
        padding: '8px 16px', borderRadius: '8px',
        border: '1px solid #334155', background: 'rgba(30,41,59,0.8)',
        color: '#64748b', fontSize: '13px', cursor: 'pointer',
        fontFamily: 'Inter, system-ui, sans-serif', zIndex: 100001,
      }}>
        Skip calibration
      </button>

      {/* CSS animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.15); }
        }
      `}</style>
    </div>
  )
}
